import React, { useState, useRef, useMemo } from 'react';
import { Card, Button, AgGridTable, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { FiList, FiSearch, FiX, FiDownload } from 'react-icons/fi';
import { STATUS_COLORS } from './WaiverStatusChart';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [date] = String(iso).split('T');
  const [y, m, d] = date.split('-');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y}`;
};

// Server-side searchable / sortable / paginated records grid (AgGridTable)
// with Excel export. Sorting is delegated to the API via onSortChange —
// column `field` names match the backend DETAILED_SORT_COLUMNS whitelist.
const WaiverDetailedTable = ({
  rows, totalRows, page, perPage, loading, error,
  search,
  onPageChange, onPerPageChange, onSearchChange, onSortChange, onExport, exportLoading,
}) => {
  const [searchInput, setSearchInput] = useState(search || '');
  const searchTimer = useRef(null);
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const handleSearchInput = (value) => {
    setSearchInput(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearchChange(value), 400);
  };

  const columnDefs = useMemo(() => ([
    {
      // Pinned columns must opt out of the grid-wide flex sizing (flex: 1 in
      // AgGridTable's defaultColDef), otherwise they absorb spare width and
      // minWidth has no effect — fix the width instead.
      headerName: 'Waiver', field: 'waiver_number', width: 140, minWidth: 100, flex: 0, pinned: 'left',
      cellRenderer: (p) => (
        <span className="tw-inline-flex tw-items-center">
          {p.data?.deal_id ? (
            <a href={`/crm/projects/${p.data.deal_id}/waivers/${p.data.id}`} target="_blank" rel="noreferrer"
              className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">
              {p.value}
            </a>
          ) : <span className="tw-font-mono tw-font-bold tw-text-gray-700">{p.value}</span>}
          {p.data?.overdue && (
            <span className="tw-ml-1.5 tw-text-[10px] tw-leading-none tw-font-bold tw-px-1.5 tw-py-1 tw-rounded-xl tw-bg-orange-100 tw-text-orange-600">OVD</span>
          )}
        </span>
      ),
    },
    { headerName: 'Project', field: 'project_name', minWidth: 200, tooltipField: 'project_name',
      cellRenderer: (p) => (
        <span>
          {p.data?.deal_id ? (
            <a href={`/crm/projects/${p.data.deal_id}`} target="_blank" rel="noreferrer"
              className="hover:tw-underline">
              {p.value || '—'}
            </a>
          ) : <span className="tw-text-gray-700">{p.value || '—'}</span>}
        </span>
      ),
     },
    {
      headerName: 'P/J Number', field: 'j_number', minWidth: 120,
      valueGetter: (p) => p.data?.j_number || p.data?.q_number || '—',
      cellClass: 'tw-font-mono',
    },
    {
      headerName: 'Status', field: 'status', minWidth: 160,
      // tw-leading-none: ag-cell sets line-height = rowHeight (38px); without
      // resetting it the pill inherits that and renders as a huge badge.
      cellRenderer: (p) => (
        <span className="tw-inline-flex tw-items-center">
          <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-xl tw-text-[11px] tw-leading-none tw-font-semibold"
            style={{ background: `${STATUS_COLORS[p.data?.status_id] || '#94A3B8'}1f`, color: STATUS_COLORS[p.data?.status_id] || '#64748B' }}>
            {p.value}
          </span>
          {p.data?.closed && <span className="tw-ml-1 tw-text-[10px] tw-leading-none tw-text-gray-400">closed</span>}
        </span>
      ),
    },
    { headerName: 'Commitment', field: 'commitment_date', minWidth: 125, valueFormatter: (p) => fmtDate(p.value) },
    { headerName: 'Created',    field: 'created_at',      minWidth: 125, valueFormatter: (p) => fmtDate(p.value) },
    {
      headerName: 'CSE Incharge', field: 'cse_incharge', minWidth: 140, sortable: false,
      valueGetter: (p) => (p.data?.cse_incharge || []).join(', ') || '—',
    },
    { headerName: 'Sales Incharge', field: 'sales_incharge', minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Sales Unit',     field: 'unit_name',      minWidth: 110, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Region',         field: 'region',         minWidth: 110, valueFormatter: (p) => p.value || '—' },
    {
      headerName: 'Value', field: 'value', minWidth: 110,
      valueFormatter: (p) => fmt(p.value),
      cellClass: 'tw-tabular-nums tw-text-right',
    },
  ]), []);

  // AgGridTable reports header-click sorting via onSortChanged({colId, sort})
  const handleSortChanged = ({ colId, sort }) => {
    if (!colId || !sort) return;
    onSortChange(colId, sort === 'asc' ? 'ASC' : 'DESC');
  };

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <FiList size={15} className="tw-text-primary-500" />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">Waiver Records</h3>
        <span className="tw-text-[10px] tw-text-gray-400">{fmt(totalRows)} records</span>

        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
          {/* Server-side quick search */}
          <div className="tw-relative">
            <FiSearch className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400" size={13} />
            <input
              type="text"
              placeholder="Search waiver / project / P-J…"
              value={searchInput}
              onChange={e => handleSearchInput(e.target.value)}
              className="tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-xs tw-w-56 tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400"
            />
            {searchInput && (
              <button type="button" onClick={() => handleSearchInput('')}
                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer">
                <FiX size={12} />
              </button>
            )}
          </div>

          {/* Excel export (server side, current filters/search/sort) */}
          <Button variant="secondary" size="sm" leftIcon={FiDownload} loading={exportLoading} onClick={onExport}
            className="tw-text-emerald-700 hover:tw-bg-emerald-50 hover:tw-border-emerald-300">
            Export Excel
          </Button>
        </div>
      </div>

      {error ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">Failed to load records</p>
      ) : (
        <div className="tw-p-3">
          {/* Fixed height — rows scroll inside the grid, header stays pinned */}
          <AgGridTable
            rowData={rows}
            columnDefs={columnDefs}
            loading={loading}
            pagination={false}
            filter={false}
            sort
            rowHeight={38}
            height="480px"
            onSortChanged={handleSortChanged}
            enableCellTextSelection
          />

          <Pagination
            className="tw-mt-3"
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={perPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPerPageChange}
            pageSizeOptions={[25, 50, 100, 200]}
            disabled={loading}
          />
        </div>
      )}
    </Card>
  );
};

WaiverDetailedTable.propTypes = {
  rows: PropTypes.array, totalRows: PropTypes.number, page: PropTypes.number,
  perPage: PropTypes.number, loading: PropTypes.bool, error: PropTypes.any,
  search: PropTypes.string, sortBy: PropTypes.string, sortDir: PropTypes.string,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool,
};
WaiverDetailedTable.defaultProps = {
  rows: [], totalRows: 0, page: 1, perPage: 50, loading: false, error: null,
  search: '', sortBy: 'created_at', sortDir: 'DESC', exportLoading: false,
};

export default WaiverDetailedTable;
